// frontend/src/pages/branch/BranchPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './BranchPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://moyobed.pro';

function BranchPage({ user }) {
  const { branchId } = useParams();
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [deadline, setDeadline] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    const bg = document.querySelector('.parallax-bg');
    if (bg) {
      bg.style.pointerEvents = 'none';
      bg.style.zIndex = '0';
    }
    fetchBranch();
  }, [branchId]);

  const fetchBranch = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/branches/${branchId}`);
      setBranch(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки филиала');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadResult(null);
    setError('');
    
    const formData = new FormData();
    formData.append('company_id', user.company_id);
    formData.append('branch_id', branchId);
    if (deadline) formData.append('deadline', deadline);
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/upload-menu-branch`, formData);
      setUploadResult({
        message: `✅ ${response.data.message}`,
        is_delete: false,
        data: response.data
      });
      alert(`✅ Меню загружено!\nБлюд: ${response.data.dishes_created}\nПунктов меню: ${response.data.menu_items_created}`);
      await fetchBranch();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки меню');
      alert('❌ Ошибка загрузки меню: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteMenu = async () => {
    if (!window.confirm('🗑️ Удалить всё меню для этого филиала?')) return;
    
    setUploading(true);
    setError('');
    setUploadResult(null);
    
    try {
      const response = await axios.delete(`${API_URL}/api/admin/branches/${branchId}/menu`, {
        params: { company_id: user.company_id }
      });
      setUploadResult({
        message: `🗑️ ${response.data.message}`,
        is_delete: true,
        menu_deleted: response.data.menu_deleted,
        orders_deleted: response.data.orders_deleted
      });
      await fetchBranch();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления меню');
      alert('❌ Ошибка удаления меню: ' + (err.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setUploading(false);
    }
  };

  const triggerFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  if (loading) {
    return (
      <div className="branch-page-loading">
        <div className="loading-spinner"></div>
        <p>Загрузка филиала...</p>
      </div>
    );
  }

  if (error || !branch) {
    return (
      <div className="branch-page-error">
        <h3>❌ Ошибка</h3>
        <p>{error || 'Филиал не найден'}</p>
        <button className="btn" onClick={() => window.location.href = '/'}>🏠 На главную</button>
      </div>
    );
  }

  return (
    <div className="branch-page">
      <div className="branch-page-header">
        <h2>🏢 {branch.name}</h2>
        <div className="branch-page-actions">
          <input
            type="file"
            accept=".xlsx,.xls"
            ref={fileInputRef}
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <button 
            className="btn btn-sm" 
            onClick={triggerFileUpload}
            disabled={uploading}
          >
            {uploading ? '⏳ Загрузка...' : '📤 Загрузить меню'}
          </button>
          <button 
            className="btn btn-sm btn-danger" 
            onClick={handleDeleteMenu}
            disabled={uploading}
            title="Удалить всё меню филиала"
          >
            🗑️ Удалить меню
          </button>
          <button 
            className="btn btn-sm btn-outline" 
            onClick={async () => { try { const formData = new FormData(); formData.append("company_id", user.company_id); formData.append("branch_id", branchId); const res = await axios.post(`${API_URL}/api/admin/export-orders-by-branch`, formData); window.open(`${API_URL}${res.data.download_url}`, "_blank"); } catch(err) { alert("Ошибка: " + (err.response?.data?.detail || err.message)); } }}
          >
            📥 Заказы
          </button>
          <button 
            className="btn btn-sm btn-outline" 
            onClick={() => window.location.href = '/'}
          >
            🏠 На главную
          </button>
        </div>
      </div>

      {uploadResult && (
        <div className={uploadResult.is_delete ? 'warning-message' : 'success-message'} style={{ marginBottom: '16px' }}>
          {uploadResult.message}
          {uploadResult.is_delete && (
            <>
              <br />
              <small>Удалено пунктов меню: {uploadResult.menu_deleted || 0}</small>
              {uploadResult.orders_deleted !== undefined && (
                <>
                  <br />
                  <small>Удалено заказов: {uploadResult.orders_deleted || 0}</small>
                </>
              )}
            </>
          )}
          {!uploadResult.is_delete && uploadResult.data && (
            <>
              <br />
              <small>Блюд: {uploadResult.data.dishes_created}, пунктов меню: {uploadResult.data.menu_items_created}</small>
              {uploadResult.data.date_range && (
                <>
                  <br />
                  <small>Период: {uploadResult.data.date_range}</small>
                </>
              )}
            </>
          )}
        </div>
      )}

      {error && (
        <div className="error-message" style={{ marginBottom: '16px' }}>
          ❌ {error}
        </div>
      )}

      <div className="branch-page-info card">
        <div className="branch-page-address">📍 {branch.address}</div>
        <div className="branch-page-employees-count">👥 {branch.employee_count} сотрудников</div>
      </div>

      <div className="branch-page-employees">
        <h3>Сотрудники</h3>
        <div className="branch-employees-grid">
          {branch.employees && branch.employees.length === 0 ? (
            <div className="empty-state card"><p>Нет сотрудников в этом филиале</p></div>
          ) : (
            branch.employees.map(emp => (
              <div key={emp.id} className="branch-employee-card card">
                <div className="branch-employee-avatar">
                  {emp.initials || emp.last_name?.charAt(0) || '👤'}
                </div>
                <div className="branch-employee-name">{emp.last_name} {emp.initials}</div>
                <div className="branch-employee-phone">{emp.phone}</div>
                {emp.is_admin && <span className="branch-employee-badge">Админ</span>}
              </div>
            ))
          )}
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '30px', padding: '20px' }}>
        <button 
          className="btn" 
          onClick={() => window.location.href = '/'}
          style={{ padding: '12px 30px', fontSize: '18px', background: 'var(--gradient-1)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer' }}
        >
          🏠 На главную
        </button>
      </div>
    </div>
  );
}

export default BranchPage;
